/**
 * Normalization + validation for user-supplied links (website + social handles).
 * A website/handle ends up as an `href` on the PUBLIC business page, so the rule
 * is both semantic (must be a plausible URL) and security (never accept a
 * javascript:/data: scheme). Empty is always allowed (fields are optional).
 *
 * The backend mirrors this (business-service LinkValidator) — the frontend
 * normalization is for UX (the user sees the final value), the backend is the
 * real guard against a forged request.
 */

const DANGEROUS_SCHEME = /^\s*(javascript|data|vbscript|file):/i;
const HOST_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;
const TLD_RE = /\.[a-z]{2,}$/i;
const HANDLE_RE = /^[A-Za-z0-9_.]+$/;

export interface LinkResult {
    /** Normalized value (https://…) or null when the input was empty. */
    value: string | null;
    /** True when the input was empty or valid; false when it should be rejected. */
    ok: boolean;
}

/** Website: accept with/without scheme, normalize to https://, require a real
 *  host with a TLD, reject dangerous schemes and spaces. */
export function normalizeWebsite(raw: string | null | undefined): LinkResult {
    const s = (raw ?? '').trim();
    if (!s) return { value: null, ok: true };
    if (DANGEROUS_SCHEME.test(s) || /\s/.test(s)) return { value: null, ok: false };
    const withScheme = /^https?:\/\//i.test(s) ? s : `https://${s}`;
    try {
        const u = new URL(withScheme);
        if (u.protocol !== 'https:' && u.protocol !== 'http:') return { value: null, ok: false };
        if (!HOST_RE.test(u.hostname) || !TLD_RE.test(u.hostname)) return { value: null, ok: false };
        // Force https and drop a bare trailing slash for a clean canonical value.
        u.protocol = 'https:';
        return { value: u.toString().replace(/\/$/, ''), ok: true };
    } catch {
        return { value: null, ok: false };
    }
}

/** Social handle for a fixed domain (instagram.com / facebook.com): accept a
 *  bare @handle OR a URL of that exact domain, normalize to the full URL. */
export function normalizeSocial(raw: string | null | undefined, domain: string): LinkResult {
    let s = (raw ?? '').trim().replace(/^@+/, '');
    if (!s) return { value: null, ok: true };
    if (DANGEROUS_SCHEME.test(s)) return { value: null, ok: false };
    const looksUrl = /^https?:\/\//i.test(s) || s.includes('/') || s.includes('.');
    if (looksUrl) {
        const withScheme = /^https?:\/\//i.test(s) ? s : `https://${s}`;
        try {
            const u = new URL(withScheme);
            const host = u.hostname.replace(/^www\./i, '').toLowerCase();
            if (host !== domain) return { value: null, ok: false };
            const handle = u.pathname.replace(/^\/+|\/+$/g, '');
            if (!handle || !HANDLE_RE.test(handle)) return { value: null, ok: false };
            return { value: `https://${domain}/${handle}`, ok: true };
        } catch {
            return { value: null, ok: false };
        }
    }
    if (!HANDLE_RE.test(s)) return { value: null, ok: false };
    return { value: `https://${domain}/${s}`, ok: true };
}

export const normalizeInstagram = (raw: string | null | undefined) =>
    normalizeSocial(raw, 'instagram.com');
export const normalizeFacebook = (raw: string | null | undefined) =>
    normalizeSocial(raw, 'facebook.com');
